import { useEffect, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, IconButton, Tooltip, TextField, Divider, CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined'
import { FancyButton } from '../../comps/FancyButtons'
import CfpSelect from '../../comps/CfpSelect'
import { FS_SM, FS_XS, FS_XL, ICON_SM } from '../../common/theme'
import { saveElement } from '../../services/TemplatesService'
import { useApp, useAppActions, useAppProject } from '../../AppContext'
import { addToast } from '../../services/Toasts'

interface TaScoringTemplateModalProps {
  open: boolean
  // The scoring template to edit, OR a seed to save a new one. When
  // the object lacks an `id` / `elementid` the modal treats it as a
  // brand-new template and offers a Scope picker. When it has an id
  // the existing template is updated in place.
  template: any | null
  // Default scope (projectsha) for new templates. Empty string =
  // Global. When the template already has an `id` its own
  // `projectsha` wins; this is only consulted on the create path.
  defaultProjectSha?: string
  // Fires with the server-persisted template after a successful
  // save. Used by callers (e.g. TaEvalScored) to stamp the freshly
  // assigned id onto whatever surface launched the modal.
  onSaved?: (saved: any) => void
  onClose: () => void
}

// Stable per-row key generator so newly-added categories / checkpoints
// stay keyed even before they get a server id. Mirrors the makeId helper
// in ManageScoringTab.
function makeId(): string {
  return (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `tmp_${Math.random().toString(36).slice(2)}`
}

// Edit modal for a single scoring template - the right-hand pane of
// ManageScoringTab compressed into a dialog. The user opens it from
// TaEvalScored's "Edit Scoring Template" button so they can tweak the
// rubric without leaving the question. Categories and checkpoints are
// mutated in place against a deep-cloned draft; Save persists via
// /api2/besaveelement and patches `currentProject.scoringtemplates` so
// the next score run uses the updated template.
export default function TaScoringTemplateModal({
  open, template, defaultProjectSha = '', onSaved, onClose,
}: TaScoringTemplateModalProps) {
  const { projects } = useApp()
  const { currentProject } = useAppProject()
  const { updateProject } = useAppActions()

  // Working copy. Replaced on open, mutated as the user edits, sent to
  // the server on Save. Bumping `bump` triggers a re-render after each
  // in-place mutation without us needing to spread on every keystroke.
  const [draft, setDraft] = useState<any | null>(null)
  const [name, setName] = useState('')
  const [projectShaSel, setProjectShaSel] = useState<string>('')
  const [, bumpLocal] = useState(0)
  const tick = () => bumpLocal(n => n + 1)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !template) return
    // Deep clone so the user's edits aren't visible on the live template
    // until they hit Save (and we get back the persisted copy).
    const cloned = structuredClone(template)
    // Hydrate `categories` from `categoriesjson` if the server only
    // returned the serialised form. Matches the runtime shape the
    // rest of the app expects.
    if (!cloned.categories && cloned.categoriesjson) {
      try { cloned.categories = JSON.parse(cloned.categoriesjson) ?? [] }
      catch { cloned.categories = [] }
    }
    // Ensure every category has a stable id so the React key stays
    // valid across re-renders (server-saved ones already have ids;
    // user-added ones get a fresh one).
    cloned.categories = (cloned.categories ?? []).map((c: any) => ({
      ...c, id: c.id ?? makeId(),
    }))
    setDraft(cloned)
    // "Automatic" is the placeholder name the server hands back for
    // an unsaved auto-generated group — not a real template title.
    // Wipe it so the user has to type something meaningful before
    // saving.
    const incomingName = cloned.name ?? ''
    setName(incomingName === 'Automatic' ? '' : incomingName)
    // Existing template — keep its scope; create path — fall back to
    // the caller's default (typically the current project).
    setProjectShaSel(cloned.projectsha ?? defaultProjectSha ?? '')
  }, [open, template, defaultProjectSha])

  const isExisting = !!(draft?.id ?? draft?.elementid)
  const projectOptions = [
    { value: '', label: 'Global' },
    ...[...projects]
      .sort((a: any, b: any) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
      .map((p: any) => ({ value: p.projectsha, label: p.name })),
  ]

  if (!draft) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        </DialogContent>
      </Dialog>
    )
  }

  const handleAddCategory = () => {
    draft.categories = [
      ...(draft.categories ?? []),
      { id: makeId(), category: '', checkpoints: [] },
    ]
    tick()
  }

  const handleDeleteCategory = (catId: string) => {
    draft.categories = (draft.categories ?? []).filter((c: any) => c.id !== catId)
    tick()
  }

  const handleAddCheckpoint = (cat: any) => {
    cat.checkpoints = [...(cat.checkpoints ?? []), '']
    tick()
  }

  const handleDeleteCheckpoint = (cat: any, idx: number) => {
    cat.checkpoints = (cat.checkpoints ?? []).filter((_: string, i: number) => i !== idx)
    tick()
  }

  const handleCheckpointChange = (cat: any, idx: number, value: string) => {
    if (!cat.checkpoints) return
    cat.checkpoints[idx] = value
  }

  // "Automatic" is a reserved placeholder used by the server for the
  // auto-generated scoring group. Block it (case-insensitive) and
  // also block empty / whitespace-only names so a template always
  // has a meaningful title before it's persisted.
  const trimmedName = name.trim()
  const isAutomaticName = trimmedName.toLowerCase() === 'automatic'
  const isValidName = trimmedName.length > 0 && !isAutomaticName

  const handleSave = async () => {
    if (!draft || !isValidName) return
    setSaving(true)
    try {
      // Strip blank rows the user left in. Each checkpoint string is
      // trimmed and dropped when empty; a category with no remaining
      // checkpoints is dropped entirely so a half-filled template
      // doesn't end up on the server.
      const cleanCategories = (draft.categories ?? [])
        .map((c: any) => ({
          ...c,
          category: typeof c?.category === 'string' ? c.category.trim() : '',
          checkpoints: (c?.checkpoints ?? [])
            .map((cp: any) => typeof cp === 'string' ? cp.trim() : '')
            .filter((cp: string) => cp.length > 0),
        }))
        .filter((c: any) => c.checkpoints.length > 0)
      // Re-serialise categoriesjson alongside categories so backends
      // that key on either form stay in sync. `projectsha` carries the
      // chosen scope: empty string ⇒ Global (omit so the server
      // treats it as global), non-empty ⇒ project-scoped.
      const body: Record<string, any> = {
        ...draft,
        // `isValidName` guards the entry point so the trimmed input is
        // guaranteed non-empty and not "Automatic" by the time we get
        // here — no need for a draft-name fallback that could leak
        // the reserved placeholder back into the body.
        name: trimmedName,
        categories: cleanCategories,
        categoriesjson: JSON.stringify(cleanCategories),
        type: draft.type ?? 'score',
      }
      if (projectShaSel) body.projectsha = projectShaSel
      else delete body.projectsha
      const saved = await saveElement(body)
      const replacement = saved ?? body
      // Patch currentProject.scoringtemplates so the updated (or
      // newly-saved) template is visible immediately. Append when
      // there's no matching id (the create path), replace otherwise.
      // Preserve the project's existing `modified` flag — saveElement
      // persisted to its own endpoint, the bid itself didn't change.
      const replacementId = replacement.id ?? replacement.elementid
      const list = currentProject?.scoringtemplates ?? []
      const matchIdx = list.findIndex((t: any) => (t.id ?? t.elementid) === replacementId)
      const updated = matchIdx >= 0
        ? list.map((t: any, i: number) => i === matchIdx ? replacement : t)
        : [...list, replacement]
      updateProject({
        scoringtemplates: updated,
        modified: currentProject?.modified,
      } as any)
      addToast('success', 'Scoring template saved.')
      onSaved?.(replacement)
      onClose()
    } catch {
      addToast('error', 'Failed to save scoring template.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ 'data-testid': 'scoretpl_dlg' } as any}
    >
      <DialogTitle sx={{ px: 2.5, pt: 2, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <LayersOutlinedIcon sx={{ fontSize: ICON_SM, opacity: 0.7 }} />
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem', flex: 1 }}>
          {isExisting ? 'Edit Scoring Template' : 'Save as Scoring Template'}
        </Typography>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
            <CloseIcon sx={{ fontSize: ICON_SM }} />
          </IconButton>
        </Tooltip>
      </DialogTitle>

      <Box sx={{ px: 2.5, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1.25, alignItems: 'flex-end' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7, fontSize: FS_XS, display: 'block', mb: 0.25 }}>
            Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Template name"
            inputProps={{ 'data-testid': 'scoretpl_name_input' }}
            error={isAutomaticName}
            helperText={isAutomaticName ? '"Automatic" is reserved — choose a different name.' : undefined}
          />
        </Box>
        <Box sx={{ flex: '0 0 200px' }}>
          <Typography variant="caption" sx={{ fontWeight: 500, opacity: 0.7, fontSize: FS_XS, display: 'block', mb: 0.25 }}>
            Scope
          </Typography>
          <CfpSelect
            testId="scoretpl_scope_select"
            value={projectShaSel}
            onChange={setProjectShaSel}
            options={projectOptions}
            fullWidth
          />
        </Box>
      </Box>

      <DialogContent dividers sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, flex: 1, fontSize: FS_SM }}>Categories</Typography>
          <Tooltip title="Add Category">
            <IconButton size="small" onClick={handleAddCategory} data-testid="scoretpl_add_cat_btn">
              <AddIcon sx={{ fontSize: ICON_SM }} />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 1 }} />

        {(draft.categories ?? []).length === 0 && (
          <Typography variant="caption" sx={{ opacity: 0.45, display: 'block', px: 0.5, mb: 1 }}>
            No categories. Click + to add one.
          </Typography>
        )}

        {(draft.categories ?? []).map((cat: any) => (
          <Box key={cat.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, overflow: 'hidden' }}>
            {/* Category header */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, bgcolor: 'action.hover', gap: 0.5 }}>
              <TextField
                size="small"
                placeholder="Category name"
                defaultValue={cat.category ?? ''}
                onChange={e => { cat.category = e.target.value }}
                variant="standard"
                sx={{ flex: 1, '& input': { fontSize: FS_SM, py: 0.25 } }}
              />
              <Tooltip title="Add Checkpoint">
                <IconButton size="small" onClick={() => handleAddCheckpoint(cat)} sx={{ p: 0.25 }}>
                  <AddIcon sx={{ fontSize: FS_XL }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Category">
                <IconButton size="small" onClick={() => handleDeleteCategory(cat.id)} sx={{ p: 0.25, opacity: 0.55, '&:hover': { opacity: 1 } }}>
                  <DeleteOutlineIcon sx={{ fontSize: FS_XL }} />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Checkpoint rows */}
            <Box sx={{ p: 0.75 }}>
              {(cat.checkpoints ?? []).length === 0 && (
                <Typography variant="caption" sx={{ opacity: 0.4, display: 'block', px: 0.5 }}>
                  No checkpoints
                </Typography>
              )}
              {(cat.checkpoints ?? []).map((cp: string, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25 }}>
                  <TextField
                    size="small"
                    placeholder="Check"
                    defaultValue={cp ?? ''}
                    onChange={e => handleCheckpointChange(cat, idx, e.target.value)}
                    variant="outlined"
                    sx={{ flex: 1, '& input': { fontSize: FS_SM, py: 0.4 } }}
                  />
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDeleteCheckpoint(cat, idx)} sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                      <DeleteOutlineIcon sx={{ fontSize: FS_XL }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <FancyButton testId="scoretpl_cancel_btn" onClick={onClose}>Cancel</FancyButton>
        <FancyButton
          testId="scoretpl_save_btn"
          solid
          disabled={saving || !isValidName}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : 'Save'}
        </FancyButton>
      </DialogActions>
    </Dialog>
  )
}
